/**
 * Pure message reducer for the SSE chat stream.
 *
 *     reduceFrame(message, frame)     →  next message (intermediate / continuation)
 *     finalizeMessage(message)        →  next message (terminal final, no side effects)
 *
 * These functions are **side-effect free**: no setState, no analytics,
 * no callbacks. The hook (`useStreamSend.ts`) layers effects on top.
 *
 * They replace the imperative branches of the old `processStreamEvent` /
 * `processFinalEvent` helpers in `eventHandlers.ts`.
 */
import { Message, PartResult, RequestLine } from '../components/features/chat/types';
import { annotatedImageKey } from './annotatedImages';
import {
  ContinuationFrame,
  FinalFrame,
  IntermediateFrame,
  StreamFrame,
} from '../types/streamProtocol';

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

interface NormalisedEvent {
  type: string;
  content: string;
  timestamp?: string;
  is_final: boolean;
  has_error: boolean;
  parts_result: PartResult[];
  annotated_images: string[] | null;
  repair_instructions: string | null;
  parts_list: any[] | null;
  context_tags: string[] | null;
  not_found_parts: string[] | null;
  not_found_reasons: Record<string, string> | null;
  error_code: string | null;
  requested_parts: string[] | null;
  lines: RequestLine[] | null;
  turn_lines: string[] | null;
  search_summarization: any | null;
  ext_search_results: any | null;
  callToAction: string | null;
  cta_actions: Array<{ text?: string; request?: string; action?: string }> | null;
  vision_part_images: Array<{ part: string; image_url: string }> | null;
  transient?: boolean;
}

function buildEvent(
  frame: IntermediateFrame | ContinuationFrame | FinalFrame,
  isFinal: boolean,
): NormalisedEvent {
  return {
    type: frame.eventType,
    content: frame.content,
    timestamp: frame.timestamp,
    transient: frame.payload.transient === true,
    is_final: isFinal,
    has_error:
      (frame.kind === 'continuation' || frame.kind === 'final') && frame.hasError,
    parts_result: frame.payload.parts_result || [],
    annotated_images: frame.payload.annotated_images ?? null,
    repair_instructions: frame.payload.repair_instructions ?? null,
    parts_list: frame.payload.parts_list ?? null,
    context_tags: frame.payload.context_tags ?? null,
    not_found_parts: frame.payload.not_found_parts ?? null,
    not_found_reasons: frame.payload.not_found_reasons ?? null,
    error_code: frame.payload.error_code ?? null,
    requested_parts: frame.payload.requested_parts ?? null,
    lines: frame.payload.lines ?? null,
    turn_lines: frame.payload.turn_lines ?? null,
    search_summarization: frame.payload.search_summarization ?? null,
    ext_search_results: frame.payload.ext_search_results ?? null,
    callToAction: frame.payload.callToAction ?? null,
    cta_actions: frame.payload.cta_actions ?? null,
    vision_part_images: frame.payload.vision_part_images ?? null,
  };
}

/** Append-or-replace event in the message's event list, mirroring legacy dedup rules. */
function mergeEvent(events: Message['events'], next: NormalisedEvent): Message['events'] {
  const list = [...(events || [])];
  // Continuation steps and ack events are always unique — never deduplicate.
  const isContinuationStep = next.type.startsWith('continuation_step_');
  const isAck = next.type === 'ack';
  const existingIndex = (isContinuationStep || isAck)
    ? -1
    : list.findIndex(e => e.type === next.type && !e.is_final && !next.is_final);

  if (existingIndex === -1) {
    list.push(next);
  } else if (!next.is_final) {
    list[existingIndex] = next;
  } else {
    list.push(next);
  }
  return list;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Apply an intermediate or continuation frame to a message.
 *
 * - `intermediate` → append/replace event; preserve `hasContinuation`.
 * - `continuation` → append final event, copy payload to top-level
 *                    message fields, set `isStreaming=false` (bubble done).
 *
 * `final` and `error` frames are NOT handled here — the hook calls
 * `finalizeMessage` for `final` and routes `error` to the error path.
 */
/**
 * Every annotated image any event of this bubble carried, deduped by path.
 *
 * The backend puts the marked photo on ONE frame (the narrative / validation
 * final). Reading only the last final dropped it whenever a later final landed
 * on the same bubble or that frame was demoted to non-final — the photo then
 * appeared only after a history reload, which resolves images per invocation.
 */
function collectAnnotatedImages(events: Message['events']): string[] | null {
  const seen = new Set<string>();
  const urls: string[] = [];
  for (const e of events ?? []) {
    for (const url of (e as any)?.annotated_images ?? []) {
      const key = annotatedImageKey(url);
      if (key && !seen.has(key)) { seen.add(key); urls.push(url); }
    }
  }
  return urls.length ? urls : null;
}

export function reduceFrame(message: Message, frame: StreamFrame): Message {
  if (frame.kind !== 'intermediate' && frame.kind !== 'continuation') {
    return message;
  }

  const isFinal = frame.kind === 'continuation';
  const evt = buildEvent(frame, isFinal);
  const events = mergeEvent(message.events, evt);

  if (frame.kind === 'continuation') {
    return finalizeMessage({
      ...message,
      events,
    });
  }

  // For intermediate frames: if the event carries content (e.g. a non-final
  // "final_response" event from the vision agent that emits its JSON result
  // before the real terminal final arrives), propagate it to message.text so
  // the UI can render the identified items immediately during streaming.
  // ACKs are status lines ("Identifying VIN…", "Got it — reading your
  // request…"): they live in `events` for the StreamingIndicator and must
  // never become the bubble's text — otherwise a bubble frozen by an empty
  // continuation shows the ACK as its message (and the indicator shows it
  // again: the "duplicated message" bug).
  const updatedMessage: Message = { ...message, events };
  // Show the marked photo as soon as its frame arrives, not at stream end.
  if (evt.annotated_images?.length) {
    updatedMessage.annotated_images = collectAnnotatedImages(events);
  }
  if (
    frame.kind === 'intermediate' &&
    frame.eventType !== 'ack' &&
    frame.content &&
    frame.content.trim()
  ) {
    updatedMessage.text = frame.content;
  }

  return updatedMessage;
}

/**
 * Finalise a message at the end of the stream.
 *
 * Reads the accumulated `events` to extract the terminal final event and
 * normalises every top-level field. Returns the message with
 * `isStreaming=false`, `hasContinuation=false`, and `hasError` resolved
 * from either the SSE flag or the parsed content JSON.
 *
 * Pure: no callbacks, no state mutation. The hook should call
 * `extractBackendTags` separately if it needs to update context-tag UI.
 */
export function finalizeMessage(message: Message): Message {
  // Use the LAST `is_final` event, not the first.  A single bubble can receive
  // multiple terminal finals (e.g. an ORDER_CREATED side-effect final followed
  // by a greeting/validation final).  The most recent one is what the user
  // should actually see; older finals are typically side-effect envelopes whose
  // content (e.g. ORDER_CREATED HTML markers) would otherwise suppress the
  // bubble via `isOrderOnlyMessage`.
  const finalEvent = [...(message.events || [])].reverse().find(e => e.is_final);
  let parsedContent: any = null;
  let hasErrorFromBackend = (finalEvent as any)?.has_error === true;

  // If the terminal final event has no content, look for the last non-empty
  // content in any earlier event (e.g. vision agent emits the JSON in a
  // non-final "final_response" event, then closes the stream with an empty final).
  // (ACK events are excluded: they are status lines, not the answer.)
  const effectiveContent =
    finalEvent?.content ||
    [...(message.events || [])]
      .reverse()
      .find(e => e.type !== 'ack' && e.content && e.content.trim())?.content ||
    message.text ||
    '';

  if (effectiveContent) {
    try {
      const parsed = JSON.parse(effectiveContent);
      if (parsed && typeof parsed === 'object') {
        parsedContent = parsed;
        if (parsed.hasError === true) hasErrorFromBackend = true;
      }
    } catch {
      /* content is not JSON */
    }
  }

  // Multiple parts results
  let parts_results: PartResult[] = [];
  if (
    finalEvent?.parts_result &&
    Array.isArray(finalEvent.parts_result) &&
    finalEvent.parts_result.length > 0
  ) {
    parts_results = finalEvent.parts_result;
  } else if (finalEvent?.image || finalEvent?.parts_list) {
    // Legacy single-part fallback
    parts_results = [
      {
        image: finalEvent.image,
        image_callout: finalEvent.image_callout,
        part_callout_id: finalEvent.part_callout_id,
        structured_data: null,
      },
    ];
  }

  // Empty-response guard: a "success" final with nothing visible → treat as error.
  const isEffectivelyEmpty =
    !hasErrorFromBackend &&
    !effectiveContent.trim() &&
    parts_results.length === 0 &&
    !collectAnnotatedImages(message.events);
  if (isEffectivelyEmpty) hasErrorFromBackend = true;

  return {
    ...message,
    isStreaming: false,
    hasContinuation: false,
    currentStep: undefined,
    hasError: hasErrorFromBackend || message.hasError,
    text: effectiveContent || message.text,
    parts_results: parts_results.length > 0 ? parts_results : undefined,
    not_found_parts: finalEvent?.not_found_parts ?? null,
    not_found_reasons: finalEvent?.not_found_reasons ?? null,
    error_code: finalEvent?.error_code ?? null,
    requested_parts: finalEvent?.requested_parts ?? null,
    lines: finalEvent?.lines ?? null,
    turn_lines: finalEvent?.turn_lines ?? null,
    annotated_images: collectAnnotatedImages(message.events),
    repair_instructions: finalEvent?.repair_instructions ?? null,
    parts_list: finalEvent?.parts_list ?? null,
    context_tags: finalEvent?.context_tags ?? null,
    structured_data: parsedContent,
    search_summarization: finalEvent?.search_summarization ?? null,
    ext_search_results: finalEvent?.ext_search_results ?? null,
    callToAction: finalEvent?.callToAction ?? null,
    cta_actions: finalEvent?.cta_actions ?? null,
    vision_part_images: finalEvent?.vision_part_images ?? null,
  };
}

/**
 * Merge a trailing suggestions frame into the messages that show its cards.
 *
 * The backend ships "You might also need" AFTER the results (a model round
 * plus catalog loads the cards no longer wait for), as rows keyed by
 * session_part_uuid. Every message whose cards carry one of those uuids gets
 * that row's complementary_parts — on the rendered parts_results and on the
 * stored events, so a later finalize does not drop them.
 */
export function mergeSuggestions(messages: Message[], rows: PartResult[]): Message[] {
  const byUuid = new Map<string, PartResult['complementary_parts']>();
  for (const r of rows || []) {
    const u = (r as any)?.session_part_uuid;
    if (u && r.complementary_parts && r.complementary_parts.length) byUuid.set(String(u), r.complementary_parts);
  }
  if (!byUuid.size) return messages;
  const patch = (list?: PartResult[] | null): { list: PartResult[] | undefined; changed: boolean } => {
    if (!Array.isArray(list) || !list.length) return { list: list ?? undefined, changed: false };
    let changed = false;
    const out = list.map(pr => {
      const u = (pr as any)?.session_part_uuid;
      const parts = u ? byUuid.get(String(u)) : undefined;
      if (!parts) return pr;
      changed = true;
      return { ...pr, complementary_parts: parts };
    });
    return { list: changed ? out : list, changed };
  };
  return messages.map((msg): Message => {
    const top = patch(msg.parts_results);
    let eventsChanged = false;
    const events: Message['events'] = (msg.events || []).map(e => {
      const r = patch(e.parts_result);
      if (!r.changed) return e;
      eventsChanged = true;
      return { ...e, parts_result: r.list };
    });
    if (!top.changed && !eventsChanged) return msg;
    return { ...msg, parts_results: top.list, events: eventsChanged ? events : msg.events };
  });
}
